import { P } from 'components'
import BreadCrumbs from 'components/breadcrumbs'
import { ResponsiveActionButton } from 'components/Buttons'
import CustomButton from 'components/Buttons/CustomButton'
import LoadingAnimation from 'components/LoadingAnimation'
import PMBPagination from 'components/PMBPagination'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import { TableComponent as DataTable } from 'components/TableComponent'
import { Can } from 'context/Acl'
import { useFetchTransactionByIdQuery } from 'features/private-views/dashboard/dashboardApiSlice'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Button,
  Card,
  CardBody,
  Col,
  Modal,
  ModalBody,
  Row,
  Spinner,
} from 'reactstrap'
import { URL_DETAIL_TRANSACTION, URL_FULFILMENT_REPORT } from 'utils/pathUrl'
import { BUSINESS_INVENTORY, MANAGE } from 'utils/subjectActions'
import {
  fetchSellerFills,
  fetchSellerFillsSummary,
  toggleModal,
  unsetInventory,
} from '../reportFulfilmentSlice'
import ModalInvToTrx from './ModalInvToTrx'
import ProjectCheck from 'features/private-views/sell-commodity/ProjectCheck'
import { useSellerConfirmMutation } from 'features/private-views/approval/approvalApiSlice'
import SuccessPage from 'features/public-views/order/SuccessPage'
import { ArrowRight, ChevronDown, Plus } from 'react-feather'
import { TableSkeleton } from 'components/Skeleton'

export default function DetailReportFulfilment() {
  const { id: escrowpaymentId } = useParams()
  const navigate = useNavigate()

  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const [sellerConfirm] = useSellerConfirmMutation()

  const {
    data,
    isLoading,
    refetch: refetchTransactionById,
  } = useFetchTransactionByIdQuery({ id: escrowpaymentId })

  const { tag, summaryData, adjustmentInvoice, isOpenModal } = useSelector(
    (state) => state.reportFulfilment,
  )

  const [page, setPage] = useState(0)
  const [size, setSize] = useState(10)
  const [sortby, setSortby] = useState('createdAt')
  const [sortdir, setSortdir] = useState('DESC')
  const [sellerFillData, setSellerFillData] = useState([])
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [totalItems, setTotalItems] = useState(0)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showProcessInventory, setShowProcessInventory] = useState(false)
  const [showProjectWarningModal, setShowProjectWarningModal] = useState(false)
  const [projectItems, setProjectItems] = useState({
    mismatched: [],
    untagged: [],
  })

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search)
    }, 2000)

    return () => clearTimeout(handler)
  }, [search])

  useEffect(() => {
    loadSellerFills()
    dispatch(fetchSellerFillsSummary({ escrowpaymentId }))
  }, [page, size, debouncedSearch, sortby, sortdir])

  const onHandleSearchChange = (value) => {
    setSearch(value)
    setPage(0)
    setSize(size)
  }

  const handleSort = (column, direction) => {
    if (column?.sortField) {
      setSortdir(direction?.toUpperCase())
      setSortby(column.sortField)
    }
  }

  const loadSellerFills = async () => {
    setLoading(true)
    try {
      const resultAction = await dispatch(
        fetchSellerFills({
          page,
          size,
          sortby,
          sortdir,
          search: debouncedSearch,
          escrowpaymentId,
        }),
      )

      if (fetchSellerFills.fulfilled.match(resultAction)) {
        const { items, totalPages, totalItems } = resultAction.payload
        setSellerFillData(items)
        setTotalPages(totalPages)
        setTotalItems(totalItems)
      } else {
        throw new Error(resultAction.payload || 'Unknown error')
      }

      await dispatch(fetchSellerFillsSummary({ escrowpaymentId }))
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleCancelInventory = async (id) => {
    try {
      setLoading(true)
      const payload = {
        inventoryId: id,
      }
      await dispatch(unsetInventory(payload)).unwrap()
      loadSellerFills()
      enqueueSnackbar('Inventori berhasil dibatalkan.', {
        variant: 'success',
      })
    } catch (err) {
      console.error('Cancel failed:', err)
    } finally {
      setLoading(false)
    }
  }

  const transactionProjectId =
    data?.projectId ??
    data?.escrowpayment?.projectId ??
    data?.orderDetail?.projectId ??
    data?.project?.id ??
    null

  // Prefer the project code (label) so it matches the item project chips
  const transactionProjectLabel =
    data?.projects?.find((p) => String(p?.id) === String(transactionProjectId))
      ?.label ||
    data?.projectTitle ||
    data?.projectName ||
    data?.project?.label ||
    (transactionProjectId ? `Project #${transactionProjectId}` : null)

  const selectedProjectObj = {
    id: transactionProjectId,
    value: transactionProjectId,
    label: transactionProjectLabel,
  }

  const getProjectItems = async () => {
    let allItems = sellerFillData || []
    if (totalItems > allItems.length) {
      try {
        const res = await dispatch(
          fetchSellerFills({
            page: 0,
            size: totalItems,
            sortby,
            sortdir,
            escrowpaymentId,
          }),
        ).unwrap()
        if (res?.items) {
          allItems = res.items
        }
      } catch (e) {
        allItems = sellerFillData || []
      }
    }

    const withProject = allItems.map((item) => {
      const itemProjId =
        item?.projectId ??
        item?.item?.projectId ??
        item?.project?.id ??
        item?.inventory?.projectId ??
        null

      const itemProjName =
        item?.projectLabel ||
        item?.projectName ||
        item?.projectTitle ||
        item?.project?.label ||
        item?.project?.name ||
        (itemProjId ? `Project #${itemProjId}` : null)

      return {
        ...item,
        projectId: itemProjId,
        projectLabel: itemProjName,
      }
    })

    return {
      mismatched: withProject.filter(
        (item) =>
          item.projectId &&
          String(item.projectId) !== String(transactionProjectId),
      ),
      untagged: withProject.filter((item) => !item.projectId),
    }
  }

  const handleFinishFulfillment = async () => {
    if (!escrowpaymentId) {
      enqueueSnackbar('ID transaksi tidak valid', { variant: 'error' })
      return
    }

    // Item projects never change; only inform, untagged items get the transaction project on release
    if (transactionProjectId) {
      const items = await getProjectItems()
      if (items.mismatched.length > 0 || items.untagged.length > 0) {
        setProjectItems(items)
        setShowProjectWarningModal(true)
        return
      }
    }
    executeFinishFulfillment()
  }

  const executeFinishFulfillment = async () => {
    setIsSubmitting(true)

    const payload = {
      confirm: 1,
      escrowpaymentId,
    }

    try {
      await sellerConfirm(payload).unwrap()

      enqueueSnackbar('Transaksi berhasil diproses!', {
        variant: 'success',
      })

      navigate(`${URL_DETAIL_TRANSACTION}/${escrowpaymentId}?type=SELL`)
    } catch (error) {
      enqueueSnackbar(error?.data?.message || 'Terjadi kesalahan saat proses', {
        variant: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Keep the modal open so its button shows the loading state
  const handleProceed = async () => {
    await executeFinishFulfillment()
    setShowProjectWarningModal(false)
  }

  const columns = [
    {
      name: <span>Barang</span>,
      cell: (row) => {
        const itemProjId =
          row?.projectId ??
          row?.item?.projectId ??
          row?.project?.id ??
          row?.inventory?.projectId ??
          null
        const itemProjName =
          row?.projectLabel ||
          row?.projectName ||
          row?.projectTitle ||
          row?.project?.label ||
          (itemProjId ? `Project #${itemProjId}` : null)

        return (
          <div data-testid={`fulfilment-table-row-${row?.invId || row?.id}`}>
            <P size="small">{row?.nameItem || '-'}</P>
            {itemProjName && (
              <span
                className="badge mt-1"
                style={{
                  backgroundColor: '#E8F5E9',
                  color: '#2E7D32',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  padding: '2px 6px',
                  borderRadius: '4px',
                  display: 'inline-block',
                }}
              >
                {itemProjName}
              </span>
            )}
          </div>
        )
      },
    },
    {
      name: <span>Lot ID</span>,
      cell: (row) => {
        return (
          <div>
            <P size="small">{row?.lotCode || '-'}</P>
          </div>
        )
      },
    },
    {
      name: <span>Kode Inventori</span>,
      cell: (row) => {
        return (
          <div>
            <P size="small">{row?.ownerCode || '-'}</P>
          </div>
        )
      },
    },
    {
      name: <span>Bundle</span>,
      right: true,
      cell: (row) => {
        return (
          <div>
            <P size="small">{row?.bundle ?? '-'}</P>
          </div>
        )
      },
    },
    {
      name: <span>Nama Grup</span>,
      cell: (row) => {
        return (
          <div>
            <P size="small">{row?.groupLabel || '-'}</P>
          </div>
        )
      },
    },
    {
      name: <span>Jumlah</span>,
      right: true,
      cell: (row) => {
        return (
          <div>
            <P size="small">{`${row?.amount} ${row?.unit}` || '-'}</P>
          </div>
        )
      },
    },
    {
      name: <span></span>,
      cell: (row) => {
        return (
          <Can I={MANAGE} this={BUSINESS_INVENTORY}>
            <ResponsiveActionButton
              iconName="x"
              label="Batal"
              testId={`fulfilment-cancel-inventory-${row?.invId || row?.id}`}
              onClick={() => {
                handleCancelInventory(row?.invId)
              }}
            />
          </Can>
        )
      },
    },
  ]

  const renderMobileCard = (row, index) => {
    const amountStr = `${row?.amount ?? 0} ${row?.unit ?? ''}`
    const itemProjId =
      row?.projectId ??
      row?.item?.projectId ??
      row?.project?.id ??
      row?.inventory?.projectId ??
      null
    const itemProjName =
      row?.projectLabel ||
      row?.projectName ||
      row?.projectTitle ||
      row?.project?.label ||
      (itemProjId ? `Project #${itemProjId}` : null)

    return (
      <Card
        key={row?.invId || row?.id || index}
        className="mb-3 border-0 shadow-sm"
        style={{ borderRadius: '12px', overflow: 'hidden' }}
      >
        <CardBody className="p-0">
          {/* Header Section: Item name & Amount */}
          <div
            className="p-3 d-flex justify-content-between align-items-center"
            style={{
              backgroundColor: '#F9FAFB',
              borderBottom: '1px solid #F2F4F7',
            }}
          >
            <div className="fw-bold text-dark text-truncate me-2" style={{ fontSize: '0.95rem' }}>
              <div>{row?.nameItem || '-'}</div>
              {itemProjName && (
                <span
                  className="badge mt-1"
                  style={{
                    backgroundColor: '#E8F5E9',
                    color: '#2E7D32',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    padding: '2px 6px',
                    borderRadius: '4px',
                    display: 'inline-block',
                  }}
                >
                  {itemProjName}
                </span>
              )}
            </div>
            <div className="fw-bold text-primary text-nowrap" style={{ fontSize: '1rem' }}>
              {amountStr}
            </div>
          </div>

          {/* Body Section */}
          <div className="p-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted" style={{ fontSize: '0.85rem' }}>Nama Grup</span>
              <span className="fw-medium text-dark" style={{ fontSize: '0.85rem' }}>
                {row?.groupLabel || '-'}
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted" style={{ fontSize: '0.85rem' }}>Kode Inventori</span>
              <span className="fw-medium text-dark" style={{ fontSize: '0.85rem' }}>
                {row?.ownerCode || '-'}
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted" style={{ fontSize: '0.85rem' }}>Lot ID</span>
              <span className="fw-medium text-dark" style={{ fontSize: '0.85rem' }}>
                {row?.lotCode || '-'}
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="text-muted" style={{ fontSize: '0.85rem' }}>Bundle</span>
              <span className="fw-medium text-dark" style={{ fontSize: '0.85rem' }}>
                {row?.bundle ?? '-'}
              </span>
            </div>

            <div className="pt-2 border-top d-flex justify-content-end">
              <Can I={MANAGE} this={BUSINESS_INVENTORY}>
                <ResponsiveActionButton
                  iconName="x"
                  label="Batal"
                  testId={`fulfilment-cancel-inventory-${row?.invId || row?.id}`}
                  onClick={() => {
                    handleCancelInventory(row?.invId)
                  }}
                />
              </Can>
            </div>
          </div>
        </CardBody>
      </Card>
    )
  }

  const transactionItem = data
  const poNumber = transactionItem?.orderDetail?.transaction_number || transactionItem?.orderDetail?.poNumber || ''
  const statusTransaction = transactionItem?.status || ''

  const buyerName = transactionItem?.buyerDetail?.fullName || '-'
  const picName = transactionItem?.picBuyerDetail?.name || transactionItem?.picSellerDetail?.name || '-'
  const projectName = transactionItem?.projectTitle || transactionItem?.projectName || '-'

  const detailProjects = (() => {
    if (Array.isArray(transactionItem?.projects) && transactionItem.projects.length > 0) {
      const seen = new Set()
      const list = []
      transactionItem.projects.forEach((p) => {
        const id = p?.id ?? p?.projectId
        const label =
          p?.label ||
          p?.projectLabel ||
          p?.projectName ||
          (id ? `Project #${id}` : null)
        const key = id !== undefined && id !== null ? String(id) : label
        if (key && !seen.has(key)) {
          seen.add(key)
          list.push({ id, label })
        }
      })
      if (list.length > 0) return list
    }
    const legacyName =
      transactionItem?.projectTitle || transactionItem?.projectName
    const legacyId = transactionItem?.projectId
    if (legacyName || legacyId) {
      return [{ id: legacyId, label: legacyName || `Project #${legacyId}` }]
    }
    return []
  })()

  return (
    <div className="w-100 px-3">
      <div className="d-flex align-items-start gap-2 pt-3">
        <BreadCrumbs
          breadCrumbActive={poNumber}
          breadCrumbParent="Pemenuhan Order"
          breadCrumbParentLink={URL_FULFILMENT_REPORT}
        />
      </div>
      {isLoading ? (
        <div className="d-flex justify-content-center">
          <LoadingAnimation color="primary" />
        </div>
      ) : data?.escrowpayment?.status === 'NEED_CONFIRM_BY_SELLER' ? (
        <>
          <Modal
            isOpen={showProjectWarningModal}
            toggle={() => setShowProjectWarningModal(false)}
            centered
            style={{ maxWidth: '460px' }}
            data-testid="fulfilment-finish-modal"
          >
            <ModalBody>
              <h5 className="fw-bold text-md">Selesai Proses Pemenuhan</h5>
              <p className="text-sm">
                Pastikan inventori yang diproses sudah benar.
              </p>
              <div className="mb-3">
                <ProjectCheck
                  project={selectedProjectObj}
                  mismatched={projectItems.mismatched}
                  untagged={projectItems.untagged}
                  canPick={false}
                />
              </div>
              <div className="flex-centered flex-row gap-2 w-100">
                <CustomButton
                  color="secondary"
                  outline
                  className="w-50"
                  onClick={() => setShowProjectWarningModal(false)}
                  testId="fulfilment-finish-modal-cancel"
                >
                  <span className="text-normal">Batal</span>
                </CustomButton>
                <CustomButton
                  color="primary"
                  className="w-50"
                  disabled={isSubmitting}
                  onClick={handleProceed}
                  testId="fulfilment-finish-modal-submit"
                >
                  {isSubmitting ? (
                    <Spinner size="sm" color="light" />
                  ) : (
                    <span className="text-normal">Lanjutkan</span>
                  )}
                </CustomButton>
              </div>
            </ModalBody>
          </Modal>

          <ModalInvToTrx
            id={escrowpaymentId}
            onRefresh={loadSellerFills}
            isOpen={showProcessInventory}
            onClose={() => setShowProcessInventory(false)}
            items={summaryData?.items}
          />

          {/* SUMMARY */}
          <Card className="shadow-sm border-0 mb-3">
            <CardBody>
              <h5 className="mb-3">Rincian pesanan</h5>
              {poNumber && (
                <Row className="mb-2">
                  <Col xs="3" className="fw-light text-sm">
                    No. transaksi
                  </Col>
                  <Col xs="9" className="fw-semibold text-sm">
                    <div className="text-primary breadcrumb-hover">
                      <span
                        className="fw-semibold"
                        style={{
                          wordBreak: 'break-all',
                          cursor: 'pointer',
                          display: 'inline-block',
                        }}
                        onClick={() => {
                          navigate(
                            `${URL_DETAIL_TRANSACTION}/${escrowpaymentId}?type=SELL`,
                          )
                        }}
                      >
                        {poNumber}
                      </span>
                    </div>
                  </Col>
                </Row>
              )}

              <Row className="mb-2">
                <Col xs="3" className="fw-light text-sm">
                  Pembeli
                </Col>
                <Col xs="9" className="fw-semibold text-sm">
                  <span style={{ wordBreak: 'break-word' }}>{buyerName}</span>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col xs="3" className="fw-light text-sm">
                  PIC
                </Col>
                <Col xs="9" className="fw-semibold text-sm">
                  <span style={{ wordBreak: 'break-word' }}>{picName}</span>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col xs="3" className="fw-light text-sm">
                  Project
                </Col>
                <Col xs="9" className="fw-semibold text-sm">
                  {detailProjects.length === 0 ? (
                    <span style={{ wordBreak: 'break-word' }}>-</span>
                  ) : (
                    <div className="d-flex flex-wrap gap-1 align-items-center">
                      {detailProjects.map((p, idx) => (
                        <span
                          key={p.id ?? idx}
                          className="badge"
                          style={{
                            backgroundColor: '#E8F5E9',
                            color: '#2E7D32',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            padding: '3px 8px',
                            borderRadius: '4px',
                          }}
                        >
                          {p.label}
                        </span>
                      ))}
                    </div>
                  )}
                </Col>
              </Row>

              <div className="mt-3" style={{ overflowX: 'auto' }}>
                <div
                  className="d-flex flex-row gap-3"
                  style={{ minWidth: 'max-content' }}
                >
                  {summaryData?.items?.map((item, index) => (
                    <Card
                      key={index}
                      style={{ minWidth: '200px', flex: '0 0 auto' }}
                    >
                      <CardBody>
                        <div className="d-flex flex-row gap-3 align-items-center">
                          <div className="d-flex flex-row gap-2 flex-grow-1">
                            <img
                              src={item?.img ?? item?.linkFoto}
                              alt="barang"
                              className="rounded"
                              style={{
                                width: '50px',
                                height: '50px',
                                objectFit: 'cover',
                              }}
                            />
                            <div className="d-flex flex-column">
                              <span>{item?.label ?? item?.nameItem}</span>
                              <span
                                className={`fw-medium ${parseFloat(item?.invAmount) >
                                  parseFloat(item?.orderAmount)
                                  ? 'text-danger'
                                  : parseFloat(item?.invAmount) <
                                    parseFloat(item?.orderAmount)
                                    ? 'text-dark'
                                    : 'text-success'
                                  }`}
                              >
                                {item?.invAmount}/{item?.orderAmount}
                                {item?.unit}
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              </div>
            </CardBody>
          </Card>
          {/* END SUMMARY */}

          {/* tiket 8265 */}
          <Card className="shadow-sm border-0 mb-3">
            <CardBody>
              <DataTable
                columns={columns}
                sortServer
                onSort={handleSort}
                data={sellerFillData}
                progressPending={loading}
                progressComponent={<TableSkeleton rows={5} />}
                sortIcon={<ChevronDown />}
                noDataComponent={
                  <p className="my-3 fw-semibold text-muted">
                    Belum ada data.
                  </p>
                }
                pagination
                paginationServer
                renderMobileCard={renderMobileCard}
                paginationComponent={(props) => (
                  <PMBPagination
                    rowCount={totalItems}
                    currentPage={page}
                    rowsPerPage={size}
                    customRowsPerPageOptions={[10, 20, 30, 50, 100, 200]}
                    setCurrentPage={(e) => {
                      setPage(e)
                    }}
                    setRowsPerPage={(e) => {
                      setSize(e)
                    }}
                    props={props}
                  />
                )}
                subHeader
                subHeaderComponent={
                  <div className="w-100 mb-2">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="fw-semibold mb-0">Inventori diproses</h5>
                      <CustomButton
                        testId="fulfilment-add-inventory-button"
                        onClick={() => setShowProcessInventory(true)}
                        color="primary"
                      >
                        <div className="d-flex align-items-center gap-1">
                          <Plus size={16} color="#fff" />
                          <span>Tambah</span>
                        </div>
                      </CustomButton>
                    </div>
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-end gap-3">
                      {/* Search Input */}
                      <div className="flex-grow-1 flex-md-grow-0 w-md-auto" style={{ minWidth: '240px' }}>
                        <PMBSearchInputGroup
                          loading={loading}
                          label="Cari"
                          testId="fulfilment-search-input"
                          placeholder="Cari..."
                          maxWidth="100%"
                          onChange={(e) => onHandleSearchChange(e.target.value)}
                          value={search}
                        />
                      </div>

                      <div className="d-flex flex-column flex-md-row gap-2 w-md-60">
                        <Button
                          disabled={!sellerFillData?.length}
                          color="primary"
                          data-testid="fulfilment-finish-button"
                          className="w-100 w-md-auto"
                          onClick={handleFinishFulfillment}
                        >
                          {isSubmitting ? (
                            <LoadingAnimation size={'sm'} />
                          ) : (
                            <P className="mb-0 text-center">
                              Selesai Proses Pemenuhan
                            </P>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                }
              />
            </CardBody>
          </Card>
        </>
      ) : (
        <>
          <SuccessPage
            body={
              <>
                <h6 className="fw-bold mt-3">
                  Proses pemenuhan telah selesai!
                </h6>
                <CustomButton
                  className="mb-4"
                  outline
                  color="primary"
                  onClick={() =>
                    navigate(
                      `${URL_DETAIL_TRANSACTION}/${data?.escrowpaymentId ?? data?.escrowpayment?.id}?type=${data?.orderDetail?.type}`,
                    )
                  }
                >
                  {' '}
                  <ArrowRight size={15} /> Detail Transaksi
                </CustomButton>
              </>
            }
          />
        </>
      )}
    </div>
  )
}
